import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MessageCircle, MapPin, Loader2, ArrowUpRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { BRAND, WHATSAPP } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", email: "", phone: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We've received your enquiry and sent you a confirmation email.");
      setForm(EMPTY);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "border-zi-line bg-white text-zi-ink placeholder:text-zi-muted/70 focus-visible:ring-zi-accent focus-visible:ring-offset-0 h-12 rounded-xl";

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-zi-accent">
              Get in touch
            </p>
            <h2 className="mt-4 font-heading text-4xl font-bold tracking-tight text-zi-ink sm:text-5xl lg:text-6xl">
              Let's grow your business.
            </h2>
            <p className="mt-5 max-w-md font-sans text-base leading-relaxed text-zi-muted">
              Tell us a little about your business and what you need. We'll come
              back with a tailored proposal — usually within one working day.
            </p>

            <div className="mt-10 space-y-4">
              <a
                data-testid="contact-email-link"
                href={`mailto:${BRAND.email}`}
                className="flex items-center gap-4 rounded-2xl border border-zi-line bg-zi-tint2 p-4 transition-colors hover:border-zi-primary/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zi-primary/10 text-zi-primary">
                  <Mail size={20} />
                </span>
                <span>
                  <span className="block font-sans text-xs uppercase tracking-widest text-zi-muted">Email</span>
                  <span className="font-heading font-bold text-zi-ink">{BRAND.email}</span>
                </span>
              </a>
              <a
                data-testid="contact-whatsapp-link"
                href={WHATSAPP.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 rounded-2xl border border-zi-line bg-zi-tint2 p-4 transition-colors hover:border-zi-primary/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#25D366]/15 text-[#1DAA54]">
                  <MessageCircle size={20} />
                </span>
                <span>
                  <span className="block font-sans text-xs uppercase tracking-widest text-zi-muted">WhatsApp</span>
                  <span className="font-heading font-bold text-zi-ink">{WHATSAPP.display}</span>
                </span>
              </a>
              <div data-testid="contact-address" className="flex items-center gap-4 rounded-2xl border border-zi-line bg-zi-tint2 p-4">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-zi-primary/10 text-zi-primary">
                  <MapPin size={20} />
                </span>
                <span>
                  <span className="block font-sans text-xs uppercase tracking-widest text-zi-muted">Address</span>
                  <span className="font-heading font-bold text-zi-ink">{BRAND.address}</span>
                </span>
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid="contact-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-3xl border border-zi-line bg-zi-tint2 p-7 shadow-sm sm:p-9 lg:col-span-7"
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-zi-ink">Name *</Label>
                <Input id="name" data-testid="contact-input-name" value={form.name} onChange={update("name")} placeholder="Your full name" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company" className="text-zi-ink">Company</Label>
                <Input id="company" data-testid="contact-input-company" value={form.company} onChange={update("company")} placeholder="Company name" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zi-ink">Email *</Label>
                <Input id="email" type="email" data-testid="contact-input-email" value={form.email} onChange={update("email")} placeholder="you@example.com" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-zi-ink">Phone</Label>
                <Input id="phone" data-testid="contact-input-phone" value={form.phone} onChange={update("phone")} placeholder="Your phone number" className={inputClass} />
              </div>
            </div>
            <div className="mt-5 space-y-2">
              <Label htmlFor="message" className="text-zi-ink">Message *</Label>
              <Textarea id="message" data-testid="contact-input-message" value={form.message} onChange={update("message")} placeholder="Tell us what you're looking for..." rows={5} className="border-zi-line bg-white text-zi-ink placeholder:text-zi-muted/70 focus-visible:ring-zi-accent focus-visible:ring-offset-0 rounded-xl resize-none" />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-zi-primary px-6 py-4 font-heading text-base font-semibold text-white shadow-lg shadow-zi-primary/25 transition-all duration-300 hover:bg-zi-accent disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : (
                <>
                  Send enquiry
                  <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
